import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FilterBar, { type FilterGroup } from "@/pages/category/components/FilterBar";
import NewsCard from "@/components/feature/NewsCard";
import RecommendCard from "@/components/feature/RecommendCard";
import CharacterCard from "@/components/feature/CharacterCard";
import { categoryMeta, isCategoryType } from "@/lib/content";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import { recommendList } from "@/mocks/recommend";
import { characterList } from "@/mocks/characters";
import type { NewsItem, RecommendItem, CharacterItem } from "@/types/content";

const PAGE_SIZE = 6;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const resolved = type && isCategoryType(type) ? type : null;

  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<"latest" | "hot">("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setFilters({});
    setSort("latest");
    setVisible(PAGE_SIZE);
  }, [type]);

  const news = newsList as NewsItem[];
  const recommends = recommendList as RecommendItem[];
  const characters = characterList as CharacterItem[];

  const groups: FilterGroup[] = useMemo(() => {
    if (resolved === "news") {
      const months = Array.from(new Set(news.map((n) => n.date.slice(0, 7)))).sort((a, b) =>
        b.localeCompare(a),
      );
      return [{ key: "month", label: "日期", options: ["全部", ...months] }];
    }
    if (resolved === "recommend") {
      const genres = Array.from(new Set(recommends.flatMap((r) => r.genres)));
      const regions = Array.from(new Set(recommends.map((r) => r.region)));
      const years = Array.from(new Set(recommends.map((r) => r.year))).sort((a, b) =>
        b.localeCompare(a),
      );
      return [
        { key: "genre", label: "类型", options: ["全部", ...genres] },
        { key: "region", label: "地区", options: ["全部", ...regions] },
        { key: "year", label: "年份", options: ["全部", ...years] },
      ];
    }
    if (resolved === "character") {
      const works = Array.from(new Set(characters.map((c) => c.work)));
      return [
        { key: "work", label: "作品", options: ["全部", ...works] },
        { key: "gender", label: "性别", options: ["全部", "女", "男"] },
      ];
    }
    return [];
  }, [resolved, news, recommends, characters]);

  const filtered = useMemo(() => {
    const get = (key: string) => filters[key] || "全部";

    if (resolved === "news") {
      let list = [...news];
      const month = get("month");
      if (month !== "全部") list = list.filter((n) => n.date.startsWith(month));
      if (sort === "latest") list.sort((a, b) => b.date.localeCompare(a.date));
      return list.map((n) => ({ kind: "news" as const, data: n }));
    }
    if (resolved === "recommend") {
      let list = [...recommends];
      const genre = get("genre");
      const region = get("region");
      const year = get("year");
      if (genre !== "全部") list = list.filter((r) => r.genres.includes(genre));
      if (region !== "全部") list = list.filter((r) => r.region === region);
      if (year !== "全部") list = list.filter((r) => r.year === year);
      if (sort === "hot") list.sort((a, b) => b.score - a.score);
      else list.sort((a, b) => b.year.localeCompare(a.year));
      return list.map((r) => ({ kind: "recommend" as const, data: r }));
    }
    if (resolved === "character") {
      let list = [...characters];
      const work = get("work");
      const gender = get("gender");
      if (work !== "全部") list = list.filter((c) => c.work === work);
      if (gender !== "全部") list = list.filter((c) => c.gender === gender);
      return list.map((c) => ({ kind: "character" as const, data: c }));
    }
    return [];
  }, [resolved, filters, sort, news, recommends, characters]);

  usePageMeta({
    title: resolved ? `${categoryMeta[resolved].title} - 香蕉动漫` : "分类不存在 - 香蕉动漫",
    description: resolved ? categoryMeta[resolved].desc : undefined,
    keywords: resolved
      ? `${categoryMeta[resolved].title},香蕉动漫,动漫资讯,动画推荐,动漫角色`
      : undefined,
  });

  if (!resolved) {
    return (
      <div className="w-full px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的分类页面暂时无法找到，返回首页看看其他精彩内容吧。"
          linkTo="/"
          linkLabel="返回首页"
        />
      </div>
    );
  }

  const meta = categoryMeta[resolved];
  const shown = filtered.slice(0, visible);
  const activeCount = Object.values(filters).filter((v) => v && v !== "全部").length;

  const handleChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setVisible(PAGE_SIZE);
  };

  const handleReset = () => {
    setFilters({});
    setSort("latest");
    setVisible(PAGE_SIZE);
  };

  const gridClass =
    resolved === "news"
      ? "grid grid-cols-1 gap-4"
      : "grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5";

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: meta.title }]} />

      <div className="mt-4 md:mt-5 mb-5 md:mb-7">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-lg bg-primary-100 text-accent-700 flex items-center justify-center text-2xl">
            <i className={meta.icon}></i>
          </span>
          <div>
            <h1 className="font-display font-black text-2xl md:text-3xl text-foreground-950">
              {meta.title}
            </h1>
          </div>
        </div>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          {meta.desc}
        </p>
      </div>

      <div className="mb-4 md:mb-5">
        <FilterBar
          groups={groups}
          values={filters}
          onChange={handleChange}
          onReset={handleReset}
          activeCount={activeCount}
        />
      </div>

      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="text-xs md:text-sm text-foreground-500">
          共 <span className="font-bold text-foreground-900">{filtered.length}</span> 条内容
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              setSort("latest");
              setVisible(PAGE_SIZE);
            }}
            className={`rounded-full px-3 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
              sort === "latest"
                ? "bg-accent-500 text-white font-bold"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            最新发布
          </button>
          <button
            type="button"
            onClick={() => {
              setSort("hot");
              setVisible(PAGE_SIZE);
            }}
            className={`rounded-full px-3 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
              sort === "hot"
                ? "bg-accent-500 text-white font-bold"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            热门推荐
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="暂无相关内容"
          description="当前筛选条件下没有找到内容，试试调整或重置筛选条件。"
          actionLabel="重置筛选"
          onAction={handleReset}
        />
      ) : (
        <>
          <div className={gridClass}>
            {shown.map((entry) => {
              if (entry.kind === "news") return <NewsCard key={entry.data.id} item={entry.data} />;
              if (entry.kind === "recommend")
                return <RecommendCard key={entry.data.id} item={entry.data} />;
              return <CharacterCard key={entry.data.id} item={entry.data} />;
            })}
          </div>

          {visible < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
                加载更多
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}